/* <!-- DUPLICATE OF /track-record — update both files when statements change --> */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import StickyGetBook from "@/components/StickyGetBook";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell, ReferenceLine,
} from "recharts";
import { useTrackRecordStats, useEquityCurve, fmt } from "@/lib/useTrackRecordStats";

/* Track-record numbers are fetched from the admin-gated API — see useTrackRecordStats. */

// Inject noindex/nofollow for this page only. Removed on unmount so other
// pages keep their default indexing behaviour.
function useNoIndex() {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    meta.setAttribute("data-source", "track-record-embed");
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);
}

export default function TrackRecordEmbed() {
  const stats = useTrackRecordStats();
  const curve = useEquityCurve(stats);
  useNoIndex();

  if (!stats) return null;
  const S = stats.strings;

  return (
    <div className="bg-paper text-ink" data-testid="page-track-record-embed">
      {/* Non-clickable brand wordmark — no navigation */}
      <header className="border-b border-hairline">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-6 flex items-center">
          <span
            className="font-display text-xl md:text-2xl text-ink select-none"
            data-testid="embed-wordmark"
            aria-label="Biology Trader"
          >
            Biology Trader
          </span>
        </div>
      </header>

      {/* HERO */}
      <section className="border-b border-hairline">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 pt-20 md:pt-32 pb-16 md:pb-20">
          <div className="label-mono text-muted">{S.hero_label}</div>
          <h1 className="font-display mt-8 text-[2.25rem] sm:text-4xl md:text-6xl lg:text-[4.75rem] leading-[1.04] tracking-tight max-w-6xl">
            {S.hero_line1}<br />{S.hero_line2}
          </h1>
          <p className="font-display-medium text-2xl md:text-4xl mt-8 max-w-4xl">
            Real results. Tracked trade by trade. No leverage heroics, no flashy cars. Just risk management, patience, and the willingness to do nothing.
          </p>

          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-0 border-t border-l border-hairline" data-testid="headline-stats">
            {stats.headline_stats.map(([k, v]) => (
              <div key={k} className="border-r border-b border-hairline px-6 py-8 md:px-8 md:py-10">
                <div className="label-mono text-muted">{k}</div>
                <div className="font-display text-2xl md:text-4xl mt-3 text-ink">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EQUITY CURVE */}
      <section className="border-b border-hairline">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <h3 className="section-label">{S.equity_heading}</h3>
            <div className="label-mono text-muted">Compounded, not leveraged</div>
          </div>
          <div className="w-full h-[380px] md:h-[500px]" data-testid="equity-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={curve} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                <XAxis
                  dataKey="t"
                  type="number"
                  domain={[0, stats.total_trades]}
                  ticks={[0, 100, 200, 300, 400, stats.total_trades]}
                  tick={{ fill: "#525252", fontSize: 11, fontFamily: "IBM Plex Mono" }}
                  tickLine={false}
                  axisLine={{ stroke: "#E5E5E5" }}
                  tickFormatter={(v) => `#${v}`}
                />
                <YAxis
                  tick={{ fill: "#525252", fontSize: 11, fontFamily: "IBM Plex Mono" }}
                  tickLine={false}
                  axisLine={{ stroke: "#E5E5E5" }}
                  tickFormatter={fmt}
                  width={58}
                />
                <Tooltip
                  contentStyle={{ background: "#FFFFFF", border: "1px solid #0A0A0A", borderRadius: 0, padding: "8px 12px", fontFamily: "IBM Plex Mono", fontSize: 12 }}
                  cursor={{ stroke: "#059669", strokeWidth: 1, strokeDasharray: "3 3" }}
                  formatter={(v) => [`$${v.toLocaleString()}`, "Equity"]}
                  labelFormatter={(v) => `Trade #${v}`}
                />
                <Line
                  type="monotone"
                  dataKey="equity"
                  stroke="#0A0A0A"
                  strokeWidth={1.5}
                  dot={false}
                  activeDot={{ r: 4, fill: "#059669", stroke: "#059669" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* METRICS TABLE */}
      <section className="border-b border-hairline">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <h3 className="section-label">The numbers</h3>
          <dl className="mt-10 divide-y divide-hairline border-t border-hairline">
            {stats.metrics.map(([k, v]) => (
              <div key={k} className="grid grid-cols-12 items-baseline py-6" data-testid={`metric-${k.toLowerCase().replace(/\s/g, "-")}`}>
                <dt className="col-span-7 md:col-span-8 text-ink text-lg">{k}</dt>
                <dd className="col-span-5 md:col-span-4 text-right font-display text-2xl md:text-3xl text-ink">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* MONTHLY RETURNS */}
      <section className="border-b border-hairline">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <h3 className="section-label">Monthly returns</h3>
            <div className="label-mono text-muted">{S.monthly_label}</div>
          </div>
          <div className="w-full h-[280px] md:h-[340px]" data-testid="monthly-returns-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.monthly} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                <XAxis dataKey="m" tick={{ fill: "#525252", fontSize: 11, fontFamily: "IBM Plex Mono" }} tickLine={false} axisLine={{ stroke: "#E5E5E5" }} />
                <YAxis tick={{ fill: "#525252", fontSize: 11, fontFamily: "IBM Plex Mono" }} tickLine={false} axisLine={{ stroke: "#E5E5E5" }} tickFormatter={(v) => `${v}%`} width={45} />
                <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #0A0A0A", borderRadius: 0, padding: "8px 12px", fontFamily: "IBM Plex Mono", fontSize: 12 }} cursor={{ fill: "rgba(5,150,105,0.05)" }} formatter={(v) => [`${v}%`, "Return"]} />
                <ReferenceLine y={0} stroke="#0A0A0A" strokeWidth={1} />
                <Bar dataKey="r" radius={0}>
                  {stats.monthly.map((entry, i) => (
                    <Cell key={i} fill={entry.r >= 0 ? "#0A0A0A" : "#A3A3A3"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* TRADE OUTCOMES */}
      <section className="border-b border-hairline">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <h3 className="section-label">Trade outcome distribution</h3>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 border-t border-l border-hairline" data-testid="outcomes">
            {stats.outcomes.map((o) => (
              <div key={o.label} className="border-r border-b border-hairline px-6 py-10 md:px-10 md:py-14">
                <div className="label-mono text-muted">{o.label}</div>
                <div className="font-display text-5xl md:text-6xl mt-4 text-ink">{o.pct}%</div>
                <div className="mt-3 text-muted text-sm">{o.count} {S.outcomes_of}</div>
                <div className="mt-6 h-[2px] bg-hairline relative">
                  <div className="h-full bg-ink absolute left-0 top-0" style={{ width: `${o.pct}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMENTARY */}
      <section className="border-b border-hairline">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-20 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <h3 className="md:col-span-4 section-label">Real results vs. fake profits</h3>
          <div className="md:col-span-7 md:col-start-6 text-lg md:text-xl text-muted leading-[1.9] space-y-6">
            <p>{S.real_results_p1}</p>
            <p>{S.real_results_p2}</p>
          </div>
        </div>
      </section>

      <section className="border-b border-hairline">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-20 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <h3 className="md:col-span-4 section-label">Why discipline matters</h3>
          <div className="md:col-span-7 md:col-start-6 text-lg md:text-xl text-muted leading-[1.9] space-y-6">
            <p>Profit growth emerged as a byproduct of relentless discipline. Each month I documented results, studied mistakes, and built a feedback loop. Avoiding overtrading, refusing to chase losses, and embracing small wins over time created exponential compounding.</p>
            <p className="text-ink">The core lesson: methodical, unemotional execution always outperforms impulse and hope.</p>
          </div>
        </div>
      </section>

      <section className="border-b border-hairline">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-20 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <h3 className="md:col-span-4 section-label">Edge, risk, and mindset</h3>
          <div className="md:col-span-7 md:col-start-6 text-lg md:text-xl text-muted leading-[1.9] space-y-6">
            <p>{S.edge_p1}</p>
            <p>The most dramatic growth had less to do with signals or systems and more to do with shifting my psychology: embracing uncertainty, detaching from outcomes, and trusting the research and process.</p>
          </div>
        </div>
      </section>

      <section className="border-b border-hairline">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-20 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <h3 className="md:col-span-4 section-label">Simplicity above more</h3>
          <div className="md:col-span-7 md:col-start-6 text-lg md:text-xl text-muted leading-[1.9] space-y-6">
            <p>Minimalism guided my entire journey. Each process was stripped down to essentials: no new indicators, no signal clutter. I kept a small, specialized set of tools that delivered consistent reads, and removed everything that caused hesitation or distraction.</p>
            <p>Deep work and routine reviews ensured every method remained relevant and efficient. Consistent journaling and process audits replaced intuition and bias with clarity and predictability, refining both my system and my results.</p>
            <p className="text-ink">The outcome: results that scale. Robust downside protection, deliberate risk, and a durable mindset: evidence that simple, focused effort outlasts complexity and noise.</p>
          </div>
        </div>
      </section>

      {/* PROOF DOWNLOADS — the broker statements themselves. Open in a new tab, not navigation. */}
      <section className="border-b border-hairline">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-20 md:py-32">
          <div className="label-mono text-muted">Proof · Raw data</div>
          <h2 className="font-display mt-6 text-4xl md:text-6xl lg:text-[4rem] leading-[1.04] tracking-tight max-w-4xl">
            Don’t take my word for it.<br/>Download the raw statements.
          </h2>
          <p className="mt-8 max-w-2xl text-lg text-muted leading-relaxed">
            Every number on this page comes from these broker-exported documents. Open them, verify them, do your own math.
          </p>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-l border-hairline" data-testid="proof-downloads">
            <a
              href="/proof/dashboard.pdf"
              target="_blank"
              rel="noopener noreferrer"
              download="weekly_perf_acc82423144_data_dashboard_prorealtimev12.2.pdf"
              className="group border-r border-b border-hairline px-6 py-8 md:px-10 md:py-12 flex flex-col hover:bg-surface transition-colors"
              data-testid="proof-dashboard-download"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="label-mono text-muted">Document · PDF</div>
                  <div className="font-display text-2xl md:text-3xl mt-3 text-ink group-hover:text-leaf transition-colors">
                    Performance Dashboard
                  </div>
                  <p className="mt-3 text-muted text-sm md:text-base max-w-sm">
                    Full trading dashboard: equity curve, monthly returns, outcome distribution, drawdowns.
                  </p>
                </div>
                <span className="label-mono text-muted shrink-0">189 KB</span>
              </div>
              <div className="mt-10 label-mono text-ink group-hover:text-leaf transition-colors">
                Download →
              </div>
            </a>

            <a
              href="/proof/transcript.pdf"
              target="_blank"
              rel="noopener noreferrer"
              download="weekly_perf_acc82423144_data_transcript.pdf"
              className="group border-r border-b border-hairline px-6 py-8 md:px-10 md:py-12 flex flex-col hover:bg-surface transition-colors"
              data-testid="proof-transcript-download"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="label-mono text-muted">Document · PDF</div>
                  <div className="font-display text-2xl md:text-3xl mt-3 text-ink group-hover:text-leaf transition-colors">
                    Daily Return Transcript
                  </div>
                  <p className="mt-3 text-muted text-sm md:text-base max-w-sm">
                    Broker-exported daily cumulative returns across the full tracked period.
                  </p>
                </div>
                <span className="label-mono text-muted shrink-0">2.8 MB</span>
              </div>
              <div className="mt-10 label-mono text-ink group-hover:text-leaf transition-colors">
                Download →
              </div>
            </a>
          </div>

          <p className="mt-10 text-sm text-muted">
            Past performance does not guarantee future results.
          </p>
        </div>
      </section>

      {/* PRIMARY CTA — only clickable navigation on the entire page */}
      <section>
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
          <Link
            to="/free-book"
            className="inline-block text-lg font-extrabold py-4 px-10 bg-neutral-950 text-white hover:bg-neutral-800 transition-colors"
            data-testid="embed-get-the-book"
          >
            Get the book
          </Link>
        </div>
      </section>

      {/* Sticky bottom pop-out CTA — same design as /ebook banner */}
      <StickyGetBook to="/free-book" />
    </div>
  );
}
